import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { XIcon } from "@heroicons/react/solid";
import { useGate, useList } from "effector-react";

import {
  $notificationsStore,
  NotificationGate,
  notifications,
} from "../model/index";

import type { Notification } from "../model/index.d";

const VARIANTS = {
  info: {
    base: "bg-[#42A5F5E5]",
    textStyle: "text-white",
    icon: "/images/notify_info.svg",
    name: "Інфо",
  },

  error: {
    base: "bg-[#EF5350E5]",
    textStyle: "text-white",
    icon: "/images/notify_block.svg",
    name: "Помилка",
  },

  warning: {
    base: "bg-[#FFA726E5]",
    textStyle: "text-high-contrast",
    icon: "/images/notify_warning.svg",
    name: "Увага",
  },

  success: {
    base: "bg-[#66BB6AE5]",
    textStyle: "text-white",
    icon: "/images/notify_success.svg",
    name: "Успіх",
  },
};

const ToastMessage: React.FC<
  Notification & {
    lifetime?: number;
  }
> = ({ id, variant, message, lifetime = 3000 }) => {
  const handleRemove = (id: string) => () => {
    notifications.remove(id);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      notifications.remove(id as string);
    }, lifetime);

    return () => clearTimeout(timeout);
  }, [id, lifetime]);

  return (
    <div
      key={id}
      className={clsx(
        "flex flex-row shadow-notify overflow-hidden w-full h-14 border border-neutral-5",
        "rounded-md duration-100 cursor-pointer h-full",
        "transform transition-all",
        "before:block before:absolute before:w-full before:h-full before:-z-[1]",

        VARIANTS[variant].base
      )}
    >
      <div className="flex flex-row p-2 no-wrap items-center mr-4">
        <div
          className={clsx(
            "flex items-center h-6 w-6",
            "mx-auto text-xl select-none"
          )}
        >
          <img src={VARIANTS[variant].icon} alt="icon" />
        </div>
      </div>
      <p
        className={clsx(
          "my-auto text-body break-all flex",
          VARIANTS[variant].textStyle
        )}
      >
        {message}
      </p>

      <XIcon
        onClick={handleRemove(id as string)}
        className={clsx(
          "h-5 w-5 mr-2 my-auto mx-auto",
          "text-center leading-none text-lg",
          {
            "text-high-contrast": variant === "warning",
            "text-white": variant !== "warning",
          }
        )}
      />
    </div>
  );
};

export const Notifications = () => {
  useGate(NotificationGate);

  return createPortal(
    <div className="absolute bottom-20 max-w-[742px] w-[66%] flex items-end right-4 flex flex-col gap-y-2 z-2001">
      {useList($notificationsStore, ({ id, variant, message, isShowing }) => (
        <ToastMessage
          key={id}
          id={id}
          variant={variant}
          message={message}
          isShowing={isShowing}
        />
      ))}
    </div>,
    document.getElementById("notifications-root")!
  );
};
